/**
 * <purchase-selector> drives the "Choose your supply" bag-quantity pills,
 * the Subscribe & Save / One-Time Purchase cards, and the Add to Cart
 * button label. It only ever writes into the surrounding form's own
 * `quantity` / `selling_plan` inputs — cart submission itself is handled
 * entirely by the theme's existing <product-form-component>.
 *
 * Each pouch quantity + delivery frequency combination has its own Seal
 * selling plan, mapped via this block's settings (Seal has no native
 * concept of "this plan is for 2 pouches, every 6 weeks", so the theme
 * enforces that pairing — see each pill's data-plan-id-4/5/6/7). The
 * one-time price uses the Bundler discount percentages mirrored into this
 * block's settings instead, since Bundler is configured with "apply on
 * subscriptions" off and never touches the subscribe price.
 *
 * Not named "*-component" on purpose: that suffix is a ref-boundary marker
 * for @theme/component's Component class, and using it here would stop
 * <product-form-component> from finding its own `variantId`/`liveRegion`
 * refs nested inside this element.
 */

const FREQUENCIES = ['4', '5', '6', '7'];

/** @param {string} value */
const escapeHtml = (value) =>
  value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char);

class PurchaseSelector extends HTMLElement {
  connectedCallback() {
    this.basePrice = Number(this.dataset.basePrice) || 0;
    this.currency = this.dataset.currency || 'GBP';
    this.formatter = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: this.currency,
    });

    this.quantityInput = this.querySelector('[data-el="quantity-input"]');
    this.sellingPlanInput = this.querySelector('[data-el="selling-plan-input"]');
    this.pills = Array.from(this.querySelectorAll('[data-bag-option]'));
    this.optionCards = Array.from(this.querySelectorAll('[data-purchase-option]'));
    this.frequencySelect = this.querySelector('[data-el="frequency-select"]');
    this.addToCartLabel = this.querySelector('[data-el="add-to-cart-label"]');
    this.addToCartButton = this.closest('form')?.querySelector('[name="add"]') ?? null;

    if (this.addToCartLabel) {
      this.addToCartBaseLabel = this.addToCartLabel.textContent.trim();
    }

    this.state = {
      quantity: 1,
      bundlerPercent: 0,
      frequency: this.frequencySelect?.value || '4',
      plansByFrequency: {},
      mode: 'subscribe',
    };

    for (const pill of this.pills) {
      pill.addEventListener('click', () => this.selectBag(pill));
    }
    for (const card of this.optionCards) {
      card.addEventListener('click', () => this.selectOption(card));
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.selectOption(card);
      });
    }
    this.frequencySelect?.addEventListener('change', () => {
      this.state.frequency = this.frequencySelect.value;
      this.updatePrices();
    });

    const initialPill = this.pills.find((pill) => pill.classList.contains('is-selected')) ?? this.pills[0];
    if (initialPill) this.applyBagSelection(initialPill);

    const initialCard = this.optionCards.find((card) => card.classList.contains('is-selected')) ?? this.optionCards[0];
    if (initialCard) this.state.mode = initialCard.dataset.purchaseOption ?? 'subscribe';

    this.updatePrices();
  }

  /** @param {HTMLElement} pill */
  selectBag(pill) {
    for (const other of this.pills) {
      const selected = other === pill;
      other.classList.toggle('is-selected', selected);
      other.setAttribute('aria-checked', String(selected));
    }
    this.applyBagSelection(pill);
  }

  /** @param {HTMLElement} pill */
  applyBagSelection(pill) {
    this.state.quantity = Number(pill.dataset.quantity) || 1;
    this.state.bundlerPercent = Number(pill.dataset.bundlerPercent) || 0;

    const plansByFrequency = {};
    for (const freq of FREQUENCIES) {
      plansByFrequency[freq] = {
        id: pill.dataset[`planIdW${freq}`] || '',
        percent: Number(pill.dataset[`planPercentW${freq}`]) || 0,
        adjustmentType: pill.dataset[`planAdjustmentTypeW${freq}`] || 'percentage',
      };
    }
    this.state.plansByFrequency = plansByFrequency;

    if (this.quantityInput) this.quantityInput.value = String(this.state.quantity);
    this.updatePrices();
  }

  /** @param {HTMLElement} card */
  selectOption(card) {
    for (const other of this.optionCards) {
      const selected = other === card;
      other.classList.toggle('is-selected', selected);
      other.setAttribute('aria-checked', String(selected));
    }
    this.state.mode = card.dataset.purchaseOption ?? 'subscribe';
    this.updatePrices();
  }

  /**
   * Public entry point for the feeding-guide widget's "Use this plan" button:
   * selects the matching quantity pill, sets the delivery frequency, and
   * switches to Subscribe & Save. The only intentional link between the two
   * otherwise-decoupled widgets.
   *
   * @param {{quantity: number, frequency: number, summary?: string}} recommendation
   */
  applyRecommendation({ quantity, frequency, summary = '' }) {
    this.appliedPlan = { quantity, frequency: String(frequency) };
    this.setText('plan-applied-text', summary ? `Plan applied: ${summary}` : 'Plan applied');

    const pill = this.pills.find((candidate) => Number(candidate.dataset.quantity) === quantity);
    if (pill) this.selectBag(pill);

    if (this.frequencySelect && FREQUENCIES.includes(String(frequency))) {
      this.frequencySelect.value = String(frequency);
      this.state.frequency = String(frequency);
    }

    const subscribeCard = this.optionCards.find((card) => card.dataset.purchaseOption === 'subscribe');
    if (subscribeCard) this.selectOption(subscribeCard);

    this.updatePrices();
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /** @returns {{id: string, percent: number, adjustmentType: string}} the plan for the selected quantity + frequency */
  getSelectedPlan() {
    return this.state.plansByFrequency[this.state.frequency] ?? { id: '', percent: 0, adjustmentType: 'percentage' };
  }

  /**
   * Subscribe & Save total for a quantity + frequency, without changing the
   * current selection. Used by the feeding guide to show a cost per day.
   *
   * @param {number} quantity
   * @param {number | string} frequency
   * @returns {number | null} total in cents, or null when that pairing has no plan
   */
  quoteSubscription(quantity, frequency) {
    const pill = this.pills.find((candidate) => Number(candidate.dataset.quantity) === quantity);
    const id = pill?.dataset[`planIdW${frequency}`];
    if (!pill || !id) return null;

    const plan = {
      id,
      percent: Number(pill.dataset[`planPercentW${frequency}`]) || 0,
      adjustmentType: pill.dataset[`planAdjustmentTypeW${frequency}`] || 'percentage',
    };
    return Math.round(this.applySubscribeAdjustment(this.basePrice, plan) * quantity);
  }

  /**
   * @param {number} basePriceCents
   * @param {{id: string, percent: number, adjustmentType: string}} [plan] - defaults to the selected plan
   * @returns {number} per-unit price in cents after the plan's own adjustment
   */
  applySubscribeAdjustment(basePriceCents, plan = this.getSelectedPlan()) {
    if (!plan.id) return basePriceCents;

    switch (plan.adjustmentType) {
      case 'percentage':
        return basePriceCents * (1 - plan.percent / 100);
      case 'fixed_amount':
        return Math.max(basePriceCents - plan.percent * 100, 0);
      case 'price':
        return plan.percent * 100;
      default:
        return basePriceCents;
    }
  }

  updatePrices() {
    const { quantity, bundlerPercent, mode } = this.state;
    const bundlerMultiplier = 1 - bundlerPercent / 100;
    const regularTotal = this.basePrice * quantity;
    const plan = this.getSelectedPlan();

    // Bundler's quantity discount is configured "apply on subscriptions: off" —
    // it only ever applies to one-time purchases at checkout. Stacking it into
    // the subscribe price here would overstate the discount shown vs. what
    // checkout actually charges, so it's deliberately left out below.
    const oneTimeTotal = Math.round(regularTotal * bundlerMultiplier);
    this.setText('one-time-price-was', bundlerPercent > 0 ? this.formatMoney(regularTotal) : '');
    this.setText('one-time-price-now', this.formatMoney(oneTimeTotal));
    this.setText('one-time-save-badge', bundlerPercent > 0 ? `Save ${bundlerPercent}%` : '');

    const subscribePerUnit = this.applySubscribeAdjustment(this.basePrice);
    const subscribeTotal = Math.round(subscribePerUnit * quantity);
    const combinedPercent = regularTotal > 0 ? Math.round((1 - subscribeTotal / regularTotal) * 100) : 0;

    this.setText('subscribe-price-was', this.formatMoney(regularTotal));
    this.setText('subscribe-price-now', this.formatMoney(subscribeTotal));
    this.setText('subscribe-save-badge', combinedPercent > 0 ? `Save ${combinedPercent}%` : '');

    // Per-pouch prices make multi-pouch savings comparable at a glance
    const perPouchLabel = (cents) => (quantity > 1 ? `${this.formatMoney(cents)} / pouch` : '');
    this.setText('subscribe-price-per-pouch', perPouchLabel(subscribePerUnit));
    this.setText('one-time-price-per-pouch', perPouchLabel(oneTimeTotal / quantity));
    this.updatePillPrices();

    this.updateBenefits(combinedPercent);
    this.updateTerms(subscribeTotal);
    this.updateAppliedNote();

    if (this.sellingPlanInput) {
      const subscribing = mode === 'subscribe' && Boolean(plan.id);
      this.sellingPlanInput.disabled = !subscribing;
      this.sellingPlanInput.value = subscribing ? plan.id : '';
    }

    const total = mode === 'subscribe' ? subscribeTotal : oneTimeTotal;
    if (this.addToCartLabel && this.addToCartBaseLabel && this.addToCartButton && !this.addToCartButton.disabled) {
      this.addToCartLabel.textContent = `${this.addToCartBaseLabel} — ${this.formatMoney(total)}`;
    }
  }

  /** Shows "Plan applied" only while the selection still matches the feeding guide's plan. */
  updateAppliedNote() {
    const note = this.querySelector('[data-el="plan-applied"]');
    if (!note) return;

    const plan = this.appliedPlan;
    note.hidden = !(
      plan &&
      this.state.mode === 'subscribe' &&
      this.state.quantity === plan.quantity &&
      this.state.frequency === plan.frequency
    );
  }

  /** Shows each pill's per-pouch price for the current purchase mode and delivery frequency. */
  updatePillPrices() {
    const { mode, frequency } = this.state;
    for (const pill of this.pills) {
      const label = pill.querySelector('[data-el="pill-price"]');
      if (!label) continue;

      let perPouch;
      if (mode === 'subscribe') {
        perPouch = this.applySubscribeAdjustment(this.basePrice, {
          id: pill.dataset[`planIdW${frequency}`] || '',
          percent: Number(pill.dataset[`planPercentW${frequency}`]) || 0,
          adjustmentType: pill.dataset[`planAdjustmentTypeW${frequency}`] || 'percentage',
        });
      } else {
        perPouch = this.basePrice * (1 - (Number(pill.dataset.bundlerPercent) || 0) / 100);
      }
      label.textContent = `${this.formatMoney(Math.round(perPouch))} / pouch`;
    }
  }

  /**
   * Fills each benefit line's {percent} / {frequency} and **bold** markers from its template.
   * @param {number} percent - the current subscription discount
   */
  updateBenefits(percent) {
    const frequencyLabel = `${this.state.frequency} weeks`;
    for (const li of this.querySelectorAll('[data-el="subscribe-benefits"] li[data-template]')) {
      const text = li.querySelector('.purchase-selector__benefit-text');
      if (!(li instanceof HTMLElement) || !text) continue;
      const filled = escapeHtml(li.dataset.template ?? '')
        .replaceAll('{frequency}', frequencyLabel)
        .replaceAll('{percent}', String(percent));
      text.innerHTML = filled.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    }
  }

  /**
   * "£28.16 today, then every 4 weeks…" under the delivery dropdown.
   * @param {number} totalCents - today's subscription price
   */
  updateTerms(totalCents) {
    const terms = this.querySelector('[data-el="subscribe-terms"]');
    if (!(terms instanceof HTMLElement)) return;
    terms.textContent = (terms.dataset.template ?? '')
      .replaceAll('{price}', this.formatMoney(totalCents))
      .replaceAll('{frequency}', `${this.state.frequency} weeks`);
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  setText(key, value) {
    const el = this.querySelector(`[data-el="${key}"]`);
    if (el) el.textContent = value;
  }

  /** @param {number} cents */
  formatMoney(cents) {
    return this.formatter.format(cents / 100);
  }
}

if (!customElements.get('purchase-selector')) {
  customElements.define('purchase-selector', PurchaseSelector);
}
